import { Box, type SxProps, type Theme } from '@mui/material';
import { keyframes } from '@mui/system';

export type OttoAvatarProps = {
  size?: number;
  withStatusDot?: boolean;
  withReadyDot?: boolean;
  loading?: boolean;
  sx?: SxProps<Theme>;
};

const blipHalo = keyframes`
  0%   { transform: scale(1);    opacity: 0.55; }
  80%  { opacity: 0; }
  100% { transform: scale(1.4);  opacity: 0; }
`;

const logoPulse = keyframes`
  0%, 100% { transform: scale(1);    opacity: 1; }
  50%      { transform: scale(0.88); opacity: 0.55; }
`;

const LOGO_SRC = '/images/lift-logo-small.svg';

/** Light tile behind the avatar (design: #E8EFFA shell, 12px radius, blue border). */
/** Design reference: 76px tile, 21px radius, ~62% inner logo. */
const TILE_RADIUS_RATIO = 21 / 76;
const LOGO_SIZE_RATIO = 47.12 / 76;

export function OttoAvatar({
  size = 32,
  withStatusDot = false,
  withReadyDot = false,
  loading = false,
  sx,
}: OttoAvatarProps) {
  const borderRadius = size * TILE_RADIUS_RATIO;
  const logoSize = size * LOGO_SIZE_RATIO;
  const shadowY = size * (6 / 76);
  const shadowBlur = size * (22 / 76);
  const shadowRing = size * (4 / 76);

  return (
    <Box sx={{ position: 'relative', width: size, height: size, flexShrink: 0, ...sx }}>
      {loading ? (
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            inset: 0,
            borderRadius: `${borderRadius}px`,
            boxShadow: '0 0 0 2px rgba(26, 102, 224, 0.55)',
            animation: `${blipHalo} 1.4s ease-out infinite`,
            pointerEvents: 'none',
          }}
        />
      ) : null}
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: `${borderRadius}px`,
          backgroundColor: '#fff',
          boxShadow: `0 ${shadowY}px ${shadowBlur}px 0 rgba(26, 102, 224, 0.35), 0 0 0 ${shadowRing}px rgba(26, 102, 224, 0.1)`,
        }}
      >
        <Box
          component="img"
          src={LOGO_SRC}
          alt=""
          aria-hidden="true"
          sx={{
            display: 'block',
            width: logoSize,
            height: logoSize,
            objectFit: 'contain',
            animation: loading ? `${logoPulse} 1.2s ease-in-out infinite` : 'none',
          }}
        />
      </Box>
      {withStatusDot ? (
        <Box
          sx={{
            position: 'absolute',
            right: -1,
            bottom: -1,
            width: Math.max(8, Math.round(size * 0.25)),
            height: Math.max(8, Math.round(size * 0.25)),
            borderRadius: '50%',
            backgroundColor: '#22C55E',
            border: '2px solid #fff',
          }}
        />
      ) : null}
      {withReadyDot ? (
        <Box
          sx={{
            position: 'absolute',
            right: -1,
            top: -1,
            width: Math.max(8, Math.round(size * 0.22)),
            height: Math.max(8, Math.round(size * 0.22)),
            borderRadius: '50%',
            backgroundColor: '#EF4444',
            border: '2px solid #fff',
          }}
        />
      ) : null}
    </Box>
  );
}
